import { Link } from '@tanstack/react-router';
import { ExternalLinkIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import type { FileRouteTypes } from '@/routeTree.gen';

export type DataItemType = {
  label: string;
  value?: string;
  secondaryValue?: string;
  link?:
    | {
        to: FileRouteTypes['to'];
        newTab: false;
        params: {
          resourceId: string;
        };
        description?: string;
      }
    | {
        href: string;
        newTab: true;
        description?: string;
      };
};

function renderValue(value: string | undefined, link: DataItemType['link']) {
  if (value === undefined) {
    return <FormattedMessage id="global.noValue" />;
  }
  if (!link) {
    return value;
  }
  if (link.newTab) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noreferrer"
        className="hover:text-blue-500 underline cursor-pointer break-all"
      >
        {value} <ExternalLinkIcon className="inline-block ml-1" size={16} />
      </a>
    );
  }

  return (
    <Link
      to={link.to}
      params={link.params}
      className="hover:text-blue-500 underline cursor-pointer break-all"
    >
      {value}
    </Link>
  );
}

export const ResourceData = ({
  data,
}: {
  data: DataItemType[];
}): JSX.Element => {
  return (
    <div className="rounded-md border">
      <Table className="table-fixed w-full">
        <TableBody>
          {data.map(({ label, value, link, secondaryValue }) => (
            <TableRow key={label}>
              <TableCell className="border-r w-1/3">
                <span className="text-sm">{label}</span>
              </TableCell>

              {/* If there's no secondaryValue, span the rest of the row (2 columns). Otherwise, render 2 separate cells. */}
              {secondaryValue === undefined ? (
                <TableCell className="w-2/3" colSpan={2}>
                  {renderValue(value, link)}
                </TableCell>
              ) : (
                <>
                  <TableCell className="w-1/3">
                    {renderValue(value, link)}
                  </TableCell>
                  <TableCell className="border-l w-1/3">
                    <p className="text-md break-all">{secondaryValue}</p>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
